import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Sparkles, Utensils, PartyPopper, Check } from 'lucide-react';

const iconMap = {
  Heart: Heart,
  Sparkles: Sparkles,
  Utensils: Utensils,
  PartyPopper: PartyPopper,
  Ring: Heart,
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page" className="bg-[#F5F5F0]">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-[#4A5D23]/60" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <span className="font-accent text-4xl text-[#D4AF37] mb-4">What We Offer</span>
          <h1 className="font-heading text-5xl md:text-7xl font-light">Our Services</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <p className="text-[#6B7280] max-w-3xl mx-auto text-lg leading-relaxed">
            Every event we create is specifically tailored to our client's desires. We combine creativity, 
            personal attention and thoughtful design to bring your vision to life.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-[#F5F5F0]">
        <div className="container-custom">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Heart;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={service.id}
                  data-testid={`service-detail-${index}`}
                  className="grid lg:grid-cols-2 gap-12 items-center"
                >
                  <div className={isEven ? 'order-1' : 'order-1 lg:order-2'}>
                    <div className="relative overflow-hidden rounded-2xl img-hover-zoom shadow-hover">
                      <img
                        src={service.image_url}
                        alt={service.title}
                        className="w-full h-[400px] object-cover"
                      />
                    </div>
                  </div>
                  <div className={isEven ? 'order-2' : 'order-2 lg:order-1'}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-[#E8DFD0] flex items-center justify-center">
                        <IconComponent className="text-[#D4AF37]" size={24} />
                      </div>
                      <span className="font-accent text-3xl text-[#D4AF37]">0{index + 1}</span>
                    </div>
                    <h2 className="font-heading text-4xl text-[#2C2C2C] mb-6">{service.title}</h2>
                    <p className="text-[#6B7280] leading-relaxed mb-6 text-lg">{service.description}</p>
                    
                    {service.features && service.features.length > 0 && (
                      <ul className="space-y-3 mb-8">
                        {service.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-3 text-[#6B7280]">
                            <Check className="w-5 h-5 text-[#4A5D23]" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    <Link to="/contact">
                      <Button
                        data-testid={`service-inquiry-btn-${index}`}
                        className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-8"
                      >
                        Inquire About This Service
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Notice */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <span className="font-accent text-3xl text-[#D4AF37]">Tailored For You</span>
          <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2 mb-6">Custom Pricing</h2>
          <div className="decorative-line" />
          <p className="text-[#6B7280] max-w-2xl mx-auto text-lg mb-10">
            Every event is unique, and so is our pricing. We create custom quotes based on your specific needs, 
            vision, and event requirements. Contact us for a personalized consultation.
          </p>
          <Link to="/contact">
            <Button
              data-testid="pricing-inquiry-btn"
              className="bg-[#4A5D23] hover:bg-[#6B7F3A] text-white rounded-full px-10 py-6 text-lg"
            >
              Request a Custom Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
